import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Envelope,
  PaperPlaneTilt,
  Clock,
  Trash,
  X,
  Plus,
  MagnifyingGlass,
  Paperclip,
  WarningCircle,
  ArrowRight,
  Eye
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePermissions } from "@/hooks/usePermissions";

const EMAIL_TEMPLATES = {
  custom: { subject: "", body: "" },
  invoice_send: {
    subject: "Votre facture MikAuto",
    body: `<p>Bonjour,</p>
<p>Veuillez trouver ci-joint votre facture.</p>
<p>N'hesitez pas a nous contacter pour toute question.</p>
<p>Cordialement,<br/>L'equipe MikAuto Belgium</p>`
  },
  vehicle_available: {
    subject: "Vehicule disponible - MikAuto",
    body: `<p>Bonjour,</p>
<p>Nous avons le plaisir de vous informer qu'un vehicule correspondant a vos criteres est maintenant disponible.</p>
<p>N'hesitez pas a nous contacter pour organiser un essai ou obtenir plus d'informations.</p>
<p>Cordialement,<br/>L'equipe MikAuto Belgium</p>`
  },
  follow_up: {
    subject: "Suivi - MikAuto Belgium",
    body: `<p>Bonjour,</p>
<p>Nous revenons vers vous suite a notre dernier echange.</p>
<p>Avez-vous des questions ou souhaitez-vous planifier un rendez-vous ?</p>
<p>Cordialement,<br/>L'equipe MikAuto Belgium</p>`
  }
};

const TEMPLATE_LABELS = {
  custom: "Email libre",
  invoice_send: "Envoi de facture",
  vehicle_available: "Vehicule disponible",
  follow_up: "Suivi client"
};

const ComposeDialog = ({ open, onClose, onSent, prefilledTo, prefilledInvoiceId, prefilledClientId, prefilledVehicleId }) => {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [template, setTemplate] = useState("custom");
  const [invoiceId, setInvoiceId] = useState("");
  const [clientId, setClientId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [sending, setSending] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    if (open) {
      setTo(prefilledTo || "");
      setInvoiceId(prefilledInvoiceId || "");
      setClientId(prefilledClientId || "");
      setVehicleId(prefilledVehicleId || "");
      setCc("");
      if (prefilledInvoiceId) {
        setTemplate("invoice_send");
        setSubject(EMAIL_TEMPLATES.invoice_send.subject);
        setBodyHtml(EMAIL_TEMPLATES.invoice_send.body);
      } else {
        setTemplate("custom");
        setSubject("");
        setBodyHtml("");
      }
      fetchData();
    }
  }, [open, prefilledTo, prefilledInvoiceId, prefilledClientId, prefilledVehicleId]);

  const fetchData = async () => {
    try {
      const [invRes, cliRes] = await Promise.all([
        axios.get(`${API}/invoices?limit=100`),
        axios.get(`${API}/clients?limit=200`)
      ]);
      setInvoices(invRes.data?.data || invRes.data?.invoices || []);
      setClients(cliRes.data?.data || cliRes.data?.clients || []);
    } catch { /* ignore */ }
  };

  const handleTemplateChange = (val) => {
    setTemplate(val);
    const tpl = EMAIL_TEMPLATES[val];
    if (tpl) {
      setSubject(tpl.subject);
      setBodyHtml(tpl.body);
    }
  };

  const handleSend = async () => {
    if (!to || !subject) {
      toast.error("Destinataire et sujet requis");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/gmail/send`, {
        to,
        cc: cc || undefined,
        subject,
        body_html: bodyHtml,
        invoice_id: invoiceId || undefined,
        client_id: clientId || undefined,
        vehicle_id: vehicleId || undefined
      });
      toast.success("Email envoye !");
      onSent?.();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de l'envoi";
      if (msg.includes("non connecte") || msg.includes("non connecté")) {
        toast.error("Gmail non connecte. Allez dans Parametres pour connecter votre compte Gmail.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaperPlaneTilt size={20} className="text-blue-600" />
            Nouveau message
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="form-label">Modele</Label>
            <Select value={template} onValueChange={handleTemplateChange}>
              <SelectTrigger data-testid="email-template-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TEMPLATE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="form-label">Destinataire *</Label>
            <Input type="email" value={to} onChange={e => setTo(e.target.value)} placeholder="email@example.com" data-testid="email-to" />
          </div>

          <div>
            <Label className="form-label">CC</Label>
            <Input type="email" value={cc} onChange={e => setCc(e.target.value)} placeholder="cc@example.com" data-testid="email-cc" />
          </div>

          <div>
            <Label className="form-label">Sujet *</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Objet du message" data-testid="email-subject" />
          </div>

          <div>
            <Label className="form-label">Message</Label>
            <Textarea
              value={bodyHtml.replace(/<[^>]+>/g, '')}
              onChange={e => setBodyHtml(`<p>${e.target.value.replace(/\n/g, '</p><p>')}</p>`)}
              placeholder="Votre message..."
              rows={6}
              data-testid="email-body"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="form-label flex items-center gap-1"><Paperclip size={14} /> Joindre une facture</Label>
              <Select value={invoiceId || "none"} onValueChange={v => setInvoiceId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="email-invoice-select"><SelectValue placeholder="Aucune" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  {invoices.map(inv => (
                    <SelectItem key={inv.invoice_id} value={inv.invoice_id}>
                      {inv.numero} - {inv.client_nom || "N/A"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="form-label">Lier a un client</Label>
              <Select value={clientId || "none"} onValueChange={v => setClientId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="email-client-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {clients.map(cli => (
                    <SelectItem key={cli.client_id} value={cli.client_id}>
                      {cli.prenom} {cli.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
            <Button variant="outline" onClick={onClose} data-testid="email-cancel-btn">Annuler</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="email-send-btn">
              <PaperPlaneTilt size={16} className="mr-1.5" />
              {sending ? "Envoi..." : "Envoyer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Emails = () => {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedEmail, setSelectedEmail] = useState(null);

  const canSend = can("emails", "send");

  const fetchEmails = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/emails/history?page=${page}&limit=20`);
      setEmails(res.data.emails || []);
      setTotalPages(res.data.pages || 1);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [page]);

  const fetchGmailStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gmail/status`);
      setGmailConnected(res.data.connected);
    } catch { setGmailConnected(false); }
  }, []);

  useEffect(() => { fetchEmails(); fetchGmailStatus(); }, [fetchEmails, fetchGmailStatus]);

  const handleDeleteEmail = async (emailId) => {
    try {
      await axios.delete(`${API}/emails/${emailId}`);
      toast.success("Log email supprime");
      fetchEmails();
    } catch { toast.error("Erreur suppression"); }
  };

  const filteredEmails = search
    ? emails.filter(e =>
        (e.to || "").toLowerCase().includes(search.toLowerCase()) ||
        (e.subject || "").toLowerCase().includes(search.toLowerCase())
      )
    : emails;

  return (
    <div className="space-y-6" data-testid="emails-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-slate-900 dark:text-slate-100">Emails</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Historique et envoi d'emails</p>
        </div>
        <div className="flex items-center gap-3">
          {!gmailConnected && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <WarningCircle size={16} className="text-amber-600" />
              <span className="text-xs text-amber-700 dark:text-amber-300">Gmail non connecte</span>
            </div>
          )}
          {canSend && (
            <Button onClick={() => setComposeOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="compose-email-btn">
              <Plus size={16} className="mr-1.5" /> Nouveau message
            </Button>
          )}
        </div>
      </div>

      <div className="card p-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher par destinataire ou sujet..."
              className="pl-9"
              data-testid="email-search"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card p-12 text-center">
          <div className="spinner h-8 w-8 mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">Chargement...</p>
        </div>
      ) : filteredEmails.length === 0 ? (
        <div className="card p-12 text-center">
          <Envelope size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Aucun email envoye</p>
          {canSend && (
            <p className="text-xs text-slate-400 mt-1">
              {gmailConnected
                ? "Cliquez sur 'Nouveau message' pour envoyer votre premier email"
                : "Connectez Gmail dans Parametres pour commencer"}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredEmails.map(email => (
            <div
              key={email.email_id}
              className="card p-4 hover:shadow-md transition-all cursor-pointer"
              data-testid={`email-row-${email.email_id}`}
              onClick={() => setSelectedEmail(email)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <PaperPlaneTilt size={16} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{email.to}</span>
                      {email.invoice_id && (
                        <span className="px-2 py-0.5 text-[10px] rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 flex-shrink-0">
                          <Paperclip size={10} className="inline mr-0.5" /> Facture
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{email.subject}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                  <div className="text-right">
                    <p className="text-xs text-slate-400">{email.sent_at ? new Date(email.sent_at).toLocaleDateString("fr-BE", { day: "2-digit", month: "short" }) : ""}</p>
                    <p className="text-[10px] text-slate-400">{email.sent_at ? new Date(email.sent_at).toLocaleTimeString("fr-BE", { hour: "2-digit", minute: "2-digit" }) : ""}</p>
                  </div>
                  <span className="text-xs text-slate-400">{email.from_user_name}</span>
                </div>
              </div>
            </div>
          ))}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Precedent</Button>
              <span className="text-sm text-slate-500">{page} / {totalPages}</span>
              <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Suivant</Button>
            </div>
          )}
        </div>
      )}

      {/* Email detail modal */}
      <Dialog open={!!selectedEmail} onOpenChange={(v) => !v && setSelectedEmail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={18} className="text-blue-600" /> Detail de l'email
            </DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-3">
              <div className="grid grid-cols-[80px_1fr] gap-2 text-sm">
                <span className="text-slate-500 font-medium">A :</span>
                <span className="text-slate-900 dark:text-slate-100">{selectedEmail.to}</span>
                {selectedEmail.cc && <>
                  <span className="text-slate-500 font-medium">CC :</span>
                  <span className="text-slate-900 dark:text-slate-100">{selectedEmail.cc}</span>
                </>}
                <span className="text-slate-500 font-medium">Sujet :</span>
                <span className="text-slate-900 dark:text-slate-100 font-medium">{selectedEmail.subject}</span>
                <span className="text-slate-500 font-medium">Date :</span>
                <span className="text-slate-900 dark:text-slate-100">{selectedEmail.sent_at ? new Date(selectedEmail.sent_at).toLocaleString("fr-BE") : ""}</span>
                <span className="text-slate-500 font-medium">Par :</span>
                <span className="text-slate-900 dark:text-slate-100">{selectedEmail.from_user_name || "-"}</span>
                {selectedEmail.invoice_id && <>
                  <span className="text-slate-500 font-medium">Facture :</span>
                  <span className="text-purple-600">{selectedEmail.invoice_id}</span>
                </>}
              </div>
              <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: selectedEmail.body_html || "<em>Pas de contenu</em>" }} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" data-testid="delete-email-btn">
                      <Trash size={14} className="mr-1" /> Supprimer
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Supprimer ce log ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={() => { handleDeleteEmail(selectedEmail.email_id); setSelectedEmail(null); }} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button size="sm" variant="outline" onClick={() => setSelectedEmail(null)}>Fermer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={fetchEmails}
      />
    </div>
  );
};

export default Emails;
